import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Popup } from 'semantic-ui-react';
import { withTheme } from 'styled-components';
import { WaveSquare, TemperatureHalf, Magnet } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import { getAxisDirectionLabels } from '../../utils/directionLabels';
import colors from '../../../../common/styles/colors';
import { isConditionCoupled } from '../../utils/formatConverters';
import {
  translateEventDetectionSensorType,
  translateEventDetectionMetricType,
  translateEventDetectionAction,
  translateEventDetectionLogic
} from '../../utils/sensorMeasurementDisplay';
import {
  EventConfigCardContainer,
  ConditionsList,
  NaturalConditionRow,
  ConditionLogicPrefix,
  SensorIconWrapper,
  SensorNameText,
  MetricPillBadge,
  OperatorBadge,
  ValueDisplayBox,
  MinMaxGroup,
  MinMaxBox,
  MinMaxLabel,
  AxisValueGroup,
  AxisValueItem,
  AxisLabel,
  NaturalLanguageText,
  ModernActionRow,
  ActionText,
  LinkedBadge
} from './EventDetection.styled';

// Fallback hardcoded values (used when metadata is not available)
const FALLBACK_METRIC_TYPES = {
  Vibration: [
    { value: 'Accel RMS', unit: 'g' },
    { value: 'Accel Peak', unit: 'g' },
    { value: 'Velocity RMS', unit: 'mm/s' },
    { value: 'Velocity Peak', unit: 'mm/s' }
  ],
  Temperature: [
    { value: 'Temperature', unit: '°C' }
  ],
  Magnetometer: [
    { value: 'Magnetic Flux', unit: 'mT' },
    { value: 'Speed', unit: 'Hz' }
  ]
};

/**
 * Transform metadata from backend into UI format (same logic as ConditionRow.js)
 */
const transformMetadataToUI = (metadata) => {
  if (!metadata || !metadata.enums) {
    return {
      metricTypes: FALLBACK_METRIC_TYPES
    };
  }

  const { enums } = metadata;

  // Transform parameters to metric types
  const metricTypes = {};
  if (enums.parameters) {
    Object.entries(enums.parameters).forEach(([sensorTypeKey, params]) => {
      // Find the sensor type that matches this parameter key
      // The key is lowercase (e.g., 'vibration', 'temperature', 'magnetometer')
      const sensorType = (enums.sensor_types || []).find(st =>
        st.name.toLowerCase() === sensorTypeKey
      );

      if (sensorType) {
        const sensorTypeName = sensorType.name;

        metricTypes[sensorTypeName] = (params || []).map(param => ({
          // Use name directly from backend (now UI-friendly)
          value: param.name,
          unit: param.unit || ''
        }));
      }
    });
  }

  return {
    metricTypes: Object.keys(metricTypes).length > 0 ? metricTypes : FALLBACK_METRIC_TYPES
  };
};

const getUnit = (metric, metricType, metricTypes) => {
  const types = metricTypes[metric];
  if (!types) return '';
  const type = types.find(t => t.value === metricType);
  return type ? type.unit : '';
};

// Helper to get sensor icon component
const getSensorIcon = (metric) => {
  switch (metric) {
    case 'Vibration':
      return WaveSquare;
    case 'Temperature':
      return TemperatureHalf;
    case 'Magnetometer':
      return Magnet;
    default:
      return WaveSquare;
  }
};


// Range operators are the only spelled-out ones ENUM_TO_OPERATOR emits (formatConverters);
// the rest are already symbols ('>', '<', '≥', '≤', '=', '≠') and pass through unchanged.
const EVENT_OPERATOR_KEYS = {
  INSIDE: 'insideRange',
  OUTSIDE: 'outsideRange'
};

const EVENT_OPERATOR_FALLBACKS = {
  INSIDE: 'Inside Range',
  OUTSIDE: 'Outside Range'
};

// Helper to format operator for display
const formatOperator = (t, operator) => {
  const operatorKey = EVENT_OPERATOR_KEYS[operator];
  if (!operatorKey) return operator;
  return t(`sensors.eventDetection.operators.${operatorKey}`, {
    defaultValue: EVENT_OPERATOR_FALLBACKS[operator]
  });
};

const getDisplayDecimals = () => 2;

const formatValue = (value, metric) => {
  if (value === undefined || value === null) {
    return '0';
  }
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(numValue)) {
    return '0';
  }
  return numValue.toFixed(getDisplayDecimals(metric));
};

const EventConfigCard = ({ config, vibrationDirectionPreference = 1, eventMetadata, theme }) => {
  const { t } = useTranslation();
  const axisLabels = useMemo(
    () => getAxisDirectionLabels(t, vibrationDirectionPreference),
    [t, vibrationDirectionPreference]
  );
  const axisMap = { 1: 'x', 2: 'y', 3: 'z' }; // Keep for internal threshold key mapping

  // Transform metadata to UI format (same as ConditionRow)
  const { metricTypes } = useMemo(
    () => transformMetadataToUI(eventMetadata),
    [eventMetadata]
  );

  // Check if coupling is enabled
  const hasCoupling = config.coupling_config && config.coupling_config.coupled_sensor_types;
  const hasDashboardCoupling = !!(
    config.dashboard_coupling_config && config.dashboard_coupling_config.enabled
  );

  const renderNaturalValueDisplay = (condition) => {
    const { thresholds, metric, metricType, operator } = condition;
    const unit = getUnit(metric, metricType, metricTypes);
    const isRangeOperator = operator === 'OUTSIDE' || operator === 'INSIDE';

    // Determine which axes to show
    let axesToShow = [1, 2, 3];
    if (condition.axis && Array.isArray(condition.axis) && condition.axis.length > 0 && condition.axis[0] !== 0) {
      axesToShow = condition.axis.filter(a => a !== 0);
    }

    if (isRangeOperator && thresholds.min && thresholds.max) {
      if (thresholds.min.x !== undefined) {
        // Triaxial range - modern natural language format
        return (
          <MinMaxGroup>
            <MinMaxBox variant="min">
              <MinMaxLabel variant="min">{t('sensors.eventDetection.min')}</MinMaxLabel>
              <AxisValueGroup>
                {axesToShow.map((axisIndex) => {
                  const axisKey = axisMap[axisIndex];
                  return (
                    <AxisValueItem key={axisIndex}>
                      <AxisLabel>{axisLabels[axisIndex][0]}</AxisLabel>
                      <span>{formatValue(thresholds.min[axisKey], metric)}</span>
                    </AxisValueItem>
                  );
                })}
              </AxisValueGroup>
            </MinMaxBox>
            <NaturalLanguageText>–</NaturalLanguageText>
            <MinMaxBox variant="max">
              <MinMaxLabel variant="max">{t('sensors.eventDetection.max')}</MinMaxLabel>
              <AxisValueGroup>
                {axesToShow.map((axisIndex) => {
                  const axisKey = axisMap[axisIndex];
                  return (
                    <AxisValueItem key={axisIndex}>
                      <AxisLabel>{axisLabels[axisIndex][0]}</AxisLabel>
                      <span>{formatValue(thresholds.max[axisKey], metric)}</span>
                    </AxisValueItem>
                  );
                })}
              </AxisValueGroup>
            </MinMaxBox>
            <NaturalLanguageText style={{ marginLeft: '4px', fontWeight: '500' }}>{unit}</NaturalLanguageText>
          </MinMaxGroup>
        );
      }
      // Single-axis range
      return (
        <MinMaxGroup>
          <NaturalLanguageText style={{ fontWeight: '600', color: colors.black }}>
            {formatValue(thresholds.min.value, metric)}
          </NaturalLanguageText>
          <NaturalLanguageText style={{ color: '#94a3b8' }}>{t('sensors.eventDetection.to')}</NaturalLanguageText>
          <NaturalLanguageText style={{ fontWeight: '600', color: colors.black }}>
            {formatValue(thresholds.max.value, metric)}
          </NaturalLanguageText>
          <NaturalLanguageText style={{ marginLeft: '4px', color: '#94a3b8' }}>{unit}</NaturalLanguageText>
        </MinMaxGroup>
      );
    }

    if (thresholds.x !== undefined || thresholds.y !== undefined || thresholds.z !== undefined) {
      // Triaxial single values
      return (
        <MinMaxGroup>
          <MinMaxBox>
            <AxisValueGroup>
              {axesToShow.map((axisIndex) => {
                const axisKey = axisMap[axisIndex];
                return (
                  <AxisValueItem key={axisIndex}>
                    <AxisLabel>{axisLabels[axisIndex][0]}</AxisLabel>
                    <span>{formatValue(thresholds[axisKey], metric)}</span>
                  </AxisValueItem>
                );
              })}
            </AxisValueGroup>
          </MinMaxBox>
          <NaturalLanguageText style={{ marginLeft: '4px', fontWeight: '500' }}>{unit}</NaturalLanguageText>
        </MinMaxGroup>
      );
    }

    // Single value
    const value = formatValue(thresholds.value, metric);
    return (
      <MinMaxGroup>
        <NaturalLanguageText style={{ fontWeight: '600', color: colors.black }}>{value}</NaturalLanguageText>
        <NaturalLanguageText style={{ marginLeft: '4px', color: '#94a3b8' }}>{unit}</NaturalLanguageText>
      </MinMaxGroup>
    );
  };

  return (
    <EventConfigCardContainer>
      {hasDashboardCoupling && (
        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'flex-end' }}>
          <Popup
            trigger={(
              <LinkedBadge>
                <span>{t('sensors.eventDetection.coupling.linkedUpper')}</span>
              </LinkedBadge>
            )}
            content={t('sensors.eventDetection.dashboardCoupling.linkedHelp')}
            position="bottom right"
            size="small"
          />
        </div>
      )}
      <ConditionsList>
        {config.conditions.map((condition, idx) => {
          const SensorIconComponent = getSensorIcon(condition.metric);

          const isCoupled = hasCoupling && isConditionCoupled(condition, config.coupling_config);

          return (
            <NaturalConditionRow key={idx} style={{ position: 'relative' }}>
              {isCoupled && (
                <div style={{ position: 'absolute', top: '8px', right: '36px' }}>
                  <Popup
                    trigger={(
                      <LinkedBadge>
                        <span>{t('sensors.eventDetection.coupling.linkedUpper')}</span>
                      </LinkedBadge>
                    )}
                    content={t('sensors.eventDetection.coupling.conditionLinkedHelp')}
                    position="bottom right"
                    size="small"
                  />
                </div>
              )}

              <ConditionLogicPrefix>
                {idx === 0 ? t('sensors.eventDetection.if') : translateEventDetectionLogic(t, config.logic)}
              </ConditionLogicPrefix>

              <SensorIconWrapper>
                <SensorIconComponent aria-label={condition.metric} />
              </SensorIconWrapper>

              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SensorNameText>{translateEventDetectionSensorType(t, condition.metric)}</SensorNameText>
                  <MetricPillBadge>{translateEventDetectionMetricType(t, condition.metricType)}</MetricPillBadge>
                </div>

                <NaturalLanguageText>{t('sensors.eventDetection.is')}</NaturalLanguageText>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <OperatorBadge operator={condition.operator}>
                    {formatOperator(t, condition.operator)}
                  </OperatorBadge>
                  <ValueDisplayBox>
                    {renderNaturalValueDisplay(condition)}
                  </ValueDisplayBox>
                </div>
              </div>
            </NaturalConditionRow>
          );
        })}
      </ConditionsList>

      <ModernActionRow>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <NaturalLanguageText
            // No uppercase/letter-spacing: this now holds a localized word
            // (ja "ならば"), where letter-spacing adds a stray trailing gap on CJK.
            style={{
              fontSize: '11px',
              backgroundColor: `${theme.primaryColor}20`,
              padding: '2px 6px',
              borderRadius: '3px',
              color: '#64748b',
              fontWeight: '600'
            }}
          >
            {t('sensors.eventDetection.then', { defaultValue: 'THEN' })}
          </NaturalLanguageText>
          <NaturalLanguageText style={{ color: '#64748b' }}>:</NaturalLanguageText>
          <ActionText>{translateEventDetectionAction(t, config.action)}</ActionText>
        </div>
      </ModernActionRow>
    </EventConfigCardContainer>
  );
};

EventConfigCard.propTypes = {
  config: PropTypes.shape({
    id: PropTypes.string,
    conditions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      metric: PropTypes.string.isRequired,
      metricType: PropTypes.string.isRequired,
      operator: PropTypes.string.isRequired,
      thresholds: PropTypes.object.isRequired,
      axis: PropTypes.array
    })).isRequired,
    logic: PropTypes.oneOf(['AND', 'OR']).isRequired,
    action: PropTypes.string.isRequired
  }).isRequired,
  vibrationDirectionPreference: PropTypes.number,
  eventMetadata: PropTypes.object
};

EventConfigCard.defaultProps = {
  vibrationDirectionPreference: 1,
  eventMetadata: null
};

// Memoized because it's a pure presentational component that only depends on props
export default withTheme(React.memo(EventConfigCard));
